import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../components/UI/Button";
import Card from "../components/UI/Card";

const GastronomyScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState("breakfast");

  const gastronomyOptions = [
    {
      id: "breakfast",
      title: t("gastronomy.breakfast.title"),
      description: t("gastronomy.breakfast.description"),
      price: t("gastronomy.breakfast.price"),
      image: "/assets/images/villa Baidane-38.jpg",
      features: t("gastronomy.breakfast.features", { returnObjects: true }) || [
        "Traditional Moroccan bread",
        "Fresh local fruits",
        "Moroccan mint tea",
        "Coffee and juices",
        "Local honey and jams",
        "Fresh pastries",
      ],
      included: true,
    },
    {
      id: "halfBoard",
      title: t("gastronomy.halfBoard.title"),
      description: t("gastronomy.halfBoard.description"),
      price: t("gastronomy.halfBoard.price"),
      image: "/assets/images/villa Baidane-38.jpg",
      features: t("gastronomy.halfBoard.features", { returnObjects: true }) || [
        "Traditional Moroccan breakfast",
        "Authentic dinner with tagines",
        "Fresh local ingredients",
        "Vegetarian options available",
        "Cooked by local chef Nadia",
        "Dietary restrictions accommodated",
      ],
      included: false,
    },
    {
      id: "fullBoard",
      title: t("gastronomy.fullBoard.title"),
      description: t("gastronomy.fullBoard.description"),
      price: t("gastronomy.fullBoard.price"),
      image: "/assets/images/villa Baidane-38.jpg",
      features: t("gastronomy.fullBoard.features", { returnObjects: true }) || [
        "All meals included",
        "Traditional Moroccan cuisine",
        "International options",
        "Fresh salads and appetizers",
        "Desserts and local sweets",
        "Beverages throughout the day",
      ],
      included: false,
    },
  ];

  const selectedGastronomy = gastronomyOptions.find(
    (option) => option.id === selectedOption
  );

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/DSCN1670.jpg')`,
          }}
        ></div>

        {/* Floating Elements */}
        <div className="absolute top-16 left-8 w-16 h-16 bg-white/10 rounded-full animate-float"></div>
        <div
          className="absolute bottom-20 right-12 w-12 h-12 bg-accent/30 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-sofia font-bold mb-6 animate-fade-in-up">
            {t("gastronomy.title")}
          </h1>
          <p
            className="text-xl md:text-2xl font-sofia font-light animate-fade-in-up opacity-90"
            style={{ animationDelay: "0.2s" }}
          >
            {t("gastronomy.subtitle")}
          </p>
        </div>
      </section>

      {/* Gastronomy Options */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-sofia font-bold text-black mb-6">
              {t("gastronomy.optionsTitle")}
            </h2>
            <p className="text-lg font-sofia text-text-secondary max-w-3xl mx-auto">
              {t("gastronomy.optionsDescription")}
            </p>
          </div>

          {/* Option Selector */}
          <div
            className="flex flex-wrap justify-center gap-4 mb-12 animate-fade-in-up"
            style={{ animationDelay: "0.2s" }}
          >
            {gastronomyOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedOption(option.id)}
                className={`px-6 py-3 rounded-full font-sofia font-medium transition-all duration-300 transform hover:scale-105 ${
                  selectedOption === option.id
                    ? "bg-[#81736d] text-white shadow-glow"
                    : "bg-white text-text-primary hover:bg-[#81736d] border border-gray-200"
                }`}
              >
                {option.title}
                {option.included && (
                  <span className="ml-2 px-2 py-1 text-xs bg-[#81736d] text-white rounded-full">
                    {t("gastronomy.included")}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Selected Option Display */}
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-fade-in-up"
            style={{ animationDelay: "0.4s" }}
          >
            <div className="order-2 lg:order-1">
              <div
                className={`inline-block px-4 py-2 rounded-full text-sm font-sofia font-medium mb-4 ${
                  selectedGastronomy.included
                    ? "bg-green-100 text-[#81736d]"
                    : "bg-accent/10 text-[#81736d]"
                }`}
              >
                {selectedGastronomy.included
                  ? t("gastronomy.includedInStay")
                  : t("gastronomy.additionalService")}
              </div>

              <h3 className="text-4xl font-sofia font-bold text-black mb-6">
                {selectedGastronomy.title}
              </h3>

              <p className="text-lg font-sofia text-text-secondary mb-8 leading-relaxed">
                {selectedGastronomy.description}
              </p>

              <div className="space-y-3 mb-8">
                {selectedGastronomy.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center animate-slide-in-left"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-6 h-6 bg-[#81736d] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="font-sofia text-text-primary">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-3xl font-sofia font-bold text-[#81736d]">
                    {selectedGastronomy.price}
                  </div>
                  {!selectedGastronomy.included && (
                    <div className="text-sm font-sofia text-text-light">
                      {t("common.perPerson")} {t("common.perDay")}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  className="hover:scale-105 transform transition-all duration-300"
                  onClick={() => navigate("/booking")}
                >
                  {t("common.bookNow")}
                </Button>
                <Button
                  variant="outline"
                  className="hover:scale-105 transform transition-all duration-300"
                  onClick={() => navigate("/contact")}
                >
                  {t("common.learnMore")}
                </Button>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative group">
                <img
                  src={selectedGastronomy.image}
                  alt={selectedGastronomy.title}
                  className="w-full h-96 object-cover rounded-2xl shadow-large transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chef Spotlight */}
      <section
        className="py-16 "
        style={{ background: 'url("/assets/icons/top-img-bg.png") ' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-3xl font-sofia font-bold text-black mb-6">
                {t("gastronomy.chef.title")}
              </h2>
              <p className="text-lg font-sofia text-text-secondary leading-relaxed mb-6">
                {t("gastronomy.chef.description1")}
              </p>
              <p className="text-lg font-sofia text-text-secondary leading-relaxed mb-8">
                {t("gastronomy.chef.description2")}
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white mb-2">
                    15+
                  </div>
                  <div className="font-sofia text-black">
                    {t("gastronomy.chef.experience")}
                  </div>
                </div>
                <div className="text-center p-4 bg-[#81736d] rounded-lg">
                  <div className="text-2xl font-sofia font-bold text-white mb-2">
                    100%
                  </div>
                  <div className="font-sofia text-black">
                    {t("gastronomy.chef.satisfaction")}
                  </div>
                </div>
              </div>
            </div>

            <div className="animate-slide-in-right">
              <Card className="text-center">
                {/* <div className="text-6xl mb-4">👩‍🍳</div> */}
                <h3 className="text-xl font-sofia font-bold text-black mb-2">
                  {t("gastronomy.chef.name")}
                </h3>
                <p className="text-text-secondary font-sofia italic mb-4">
                  {t("gastronomy.chef.quote")}
                </p>
                <div className="flex justify-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-[#81736d]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/DSCN1677.jpg')`,
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl font-sofia font-bold mb-6">
              {t("gastronomy.cta.title")}
            </h2>
            <p className="text-lg font-sofia font-light mb-8 opacity-90">
              {t("gastronomy.cta.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#81736d] hover:bg-[#81736d]"
                onClick={() => navigate("/booking")}
              >
                {t("common.bookNow")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-[#81736d] hover:text-white"
                onClick={() => navigate("/contact")}
              >
                {t("common.contactUs")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GastronomyScreen;
